import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/Card";

const DAY_LABELS = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];

const LOCATION_LABELS = [
  "אחד העם",
  "סן מרטין",
  "שדרות צבי",
  "פרץ ברנשטיין",
  "חניוני לב הפארק",
  "שחר",
  "חן",
];

const TIME_SLOTS = [
  { label: "00:00 - 06:00", min: 0, max: 5 },
  { label: "06:00 - 08:00", min: 6, max: 7 },
  { label: "08:00 - 10:00", min: 8, max: 9 },
  { label: "10:00 - 12:00", min: 10, max: 11 },
  { label: "12:00 - 14:00", min: 12, max: 13 },
  { label: "14:00 - 16:00", min: 14, max: 15 },
  { label: "16:00 - 18:00", min: 16, max: 17 },
  { label: "18:00 - 20:00", min: 18, max: 19 },
  { label: "20:00 - 22:00", min: 20, max: 21 },
  { label: "22:00 - 24:00", min: 22, max: 23 },
];

const IL_TZ = "Asia/Jerusalem";
const COL_MIN_PX = 44;
const GAP_PX = 4;

function toIsraelDayKey(value) {
  return new Date(value).toLocaleDateString("sv-SE", { timeZone: IL_TZ });
}

function toIsraelHour(value) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IL_TZ,
    hour: "numeric",
    hour12: false,
  }).formatToParts(new Date(value));
  return Number(parts.find((p) => p.type === "hour")?.value || 0);
}

function dayParts(dayKey) {
  const [y, m, d] = dayKey.split("-").map(Number);
  const weekday = DAY_LABELS[new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay()];
  const dd = String(d).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  return { weekday, dateLabel: `${dd}.${mm}` };
}

function formatDayTooltip(dayKey) {
  const { weekday, dateLabel } = dayParts(dayKey);
  return `${weekday} ${dateLabel}`;
}

function lerpHex(a, b, t) {
  const parse = (hex) => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
  const [ar, ag, ab] = parse(a);
  const [br, bg, bb] = parse(b);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bCh = Math.round(ab + (bb - ab) * t);
  return `#${[r, g, bCh].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
}

/** Amber heat scale (empty → peak), matching prior NPL/Atarim look. */
function getHeatColor(value, max) {
  if (!value || value <= 0 || !max) return "#f9fafb";
  const t = Math.min(1, value / max);
  if (t < 0.5) return lerpHex("#fef3c7", "#fbbf24", t / 0.5);
  return lerpHex("#fbbf24", "#d97706", (t - 0.5) / 0.5);
}

function compactCount(n) {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return Number(n).toLocaleString("he-IL");
}

/**
 * Build full calendar-day columns (no truncation — scroll handles overflow).
 */
function buildHeatmapModel(dataByDuration) {
  const daySet = new Set();
  const locDayTotals = (dataByDuration || []).map(() => new Map());

  (dataByDuration || []).forEach((item, locIdx) => {
    const map = locDayTotals[locIdx];
    (item?.result || []).forEach((record) => {
      if (!record?.date) return;
      const dayKey = toIsraelDayKey(record.date);
      daySet.add(dayKey);
      map.set(dayKey, (map.get(dayKey) || 0) + (Number(record.entering) || 0));
    });
  });

  const days = Array.from(daySet).sort();
  const dayIndex = new Map(days.map((d, i) => [d, i]));

  const hourlyGrid = Array.from({ length: TIME_SLOTS.length }, () =>
    Array.from({ length: days.length }, () => 0)
  );

  (dataByDuration || []).forEach((item) => {
    (item?.result || []).forEach((record) => {
      if (!record?.date) return;
      const dayKey = toIsraelDayKey(record.date);
      const col = dayIndex.get(dayKey);
      if (col === undefined) return;
      const hour = toIsraelHour(record.date);
      const slotIdx = TIME_SLOTS.findIndex(
        (slot) => hour >= slot.min && hour <= slot.max
      );
      if (slotIdx !== -1) {
        hourlyGrid[slotIdx][col] += Number(record.entering) || 0;
      }
    });
  });

  return { days, locDayTotals, hourlyGrid };
}

export function NationalParkHeatmapChart({ deviceNames = [], dataByDuration = [] }) {
  const [viewMode, setViewMode] = useState("location");
  const [tooltip, setTooltip] = useState(null);

  const { days, locDayTotals, hourlyGrid } = useMemo(
    () => buildHeatmapModel(dataByDuration),
    [dataByDuration]
  );

  const rowLabels = useMemo(() => {
    if (viewMode === "hourly") {
      return TIME_SLOTS.map((s) => s.label);
    }
    return (dataByDuration || []).map(
      (_, i) => LOCATION_LABELS[i] || deviceNames[i] || `מיקום ${i + 1}`
    );
  }, [viewMode, dataByDuration, deviceNames]);

  const grid = useMemo(() => {
    if (viewMode === "hourly") {
      return hourlyGrid.map((row) => row.map((v) => Math.round(v)));
    }
    return (dataByDuration || []).map((_, i) => {
      const map = locDayTotals[i] || new Map();
      return days.map((dayKey) => Math.round(map.get(dayKey) || 0));
    });
  }, [viewMode, hourlyGrid, dataByDuration, locDayTotals, days]);

  const maxValue = useMemo(() => {
    let max = 0;
    grid.forEach((row) => row.forEach((v) => {
      if (v > max) max = v;
    }));
    return max;
  }, [grid]);

  const rangeHint = useMemo(() => {
    if (!days.length) return "";
    return `${formatDayTooltip(days[0])} – ${formatDayTooltip(days[days.length - 1])}`;
  }, [days]);

  // Always show cell sums; use compact formatting when columns are dense.
  const useCompactValues = days.length > 10 || viewMode === "hourly";
  const valueFontClass = useCompactValues
    ? "text-[8px] sm:text-[9px]"
    : "text-[9px] sm:text-[10px]";
  const labelStep =
    days.length <= 14 ? 1 : Math.ceil(days.length / 12);

  const gridTemplateColumns = `repeat(${Math.max(days.length, 1)}, minmax(${COL_MIN_PX}px, 1fr))`;
  const scrollMinWidth = Math.max(100, days.length * (COL_MIN_PX + GAP_PX));
  const rowCount = rowLabels.length;
  const gridMinHeight = viewMode === "hourly" ? 240 : 220;
  const gridRowTemplate = `repeat(${rowCount}, minmax(0, 1fr))`;

  return (
    <Card className="h-full flex flex-col justify-between">
      <CardHeader className="flex flex-row items-center justify-between border-b py-3 px-4 min-h-[73px] gap-2">
        <div className="flex items-center gap-1 bg-gray-100/90 p-1 rounded-lg border border-gray-200/70 text-xs shrink-0">
          <button
            type="button"
            onClick={() => setViewMode("location")}
            className={`px-2.5 py-1 rounded-md font-semibold transition-all cursor-pointer ${
              viewMode === "location"
                ? "bg-white text-blue-700 shadow-xs"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            לפי מיקום
          </button>
          <button
            type="button"
            onClick={() => setViewMode("hourly")}
            className={`px-2.5 py-1 rounded-md font-semibold transition-all cursor-pointer ${
              viewMode === "hourly"
                ? "bg-white text-blue-700 shadow-xs"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            לפי שעה
          </button>
        </div>

        <div className="text-right min-w-0">
          <CardTitle className="text-base font-bold text-gray-900">
            {viewMode === "location"
              ? "עומס לפי יום ומיקום"
              : "עומס לפי שעה ויום"}
          </CardTitle>
          <CardDescription className="text-xs text-gray-500 truncate">
            {rangeHint || "אין נתונים בטווח שנבחר"}
          </CardDescription>
        </div>
      </CardHeader>

      <CardContent className="px-2 pt-2 sm:px-4 sm:pt-3 pb-3 flex-1 flex flex-col">
        {days.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-sm text-gray-400">
            אין נתונים להצגה
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden relative min-h-[300px]" dir="ltr">
            {/* Y-axis labels — one grid row per heatmap row (00:00 at top) */}
            <div
              className={`grid flex-shrink-0 text-[10px] text-gray-600 font-medium text-right pr-2 ${
                viewMode === "location" ? "w-[6.5rem]" : "w-[5.25rem]"
              }`}
              style={{
                gridTemplateRows: gridRowTemplate,
                minHeight: gridMinHeight,
                gap: `${GAP_PX}px`,
              }}
            >
              {rowLabels.map((label) => (
                <span
                  key={label}
                  className="flex items-center justify-end leading-tight truncate"
                  title={label}
                >
                  {label}
                </span>
              ))}
            </div>

            {/* Scrollable cells + day labels (same column track = perfect alignment) */}
            <div className="flex-1 flex flex-col overflow-x-auto overflow-y-hidden pb-1">
              <div
                className="flex-1 flex flex-col min-w-max pr-1"
                style={{ minWidth: `${scrollMinWidth}px` }}
              >
                <div
                  className="flex-1 grid"
                  style={{
                    gridTemplateColumns,
                    gridTemplateRows: gridRowTemplate,
                    gap: `${GAP_PX}px`,
                    minHeight: gridMinHeight,
                  }}
                >
                  {grid.map((row, rIdx) =>
                    row.map((value, cIdx) => {
                      const dayKey = days[cIdx];
                      const key = `${rIdx}-${cIdx}`;
                      const bg = getHeatColor(value, maxValue);
                      const tipLabel = `${rowLabels[rIdx]} · ${formatDayTooltip(dayKey)}`;
                      return (
                        <div
                          key={key}
                          className="rounded-sm cursor-default relative flex items-center justify-center transition-shadow hover:ring-2 hover:ring-amber-400/70"
                          style={{ backgroundColor: bg, minHeight: 22 }}
                          onMouseEnter={() =>
                            setTooltip({ key, label: tipLabel, value })
                          }
                          onMouseLeave={() => setTooltip(null)}
                        >
                          {value > 0 && (
                            <span
                              className={`${valueFontClass} font-semibold text-gray-800 pointer-events-none select-none`}
                            >
                              {useCompactValues
                                ? compactCount(value)
                                : Number(value).toLocaleString("he-IL")}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {/* X-axis: date on top, weekday below — same columns as cells */}
                <div
                  className="grid mt-2 text-[10px] text-gray-700 text-center"
                  style={{ gridTemplateColumns, gap: `${GAP_PX}px` }}
                >
                  {days.map((dayKey, i) => {
                    const show =
                      i % labelStep === 0 || i === days.length - 1;
                    const { dateLabel, weekday } = dayParts(dayKey);
                    return (
                      <div
                        key={dayKey}
                        className="flex flex-col items-center justify-start leading-tight h-8"
                      >
                        {show && (
                          <>
                            <span className="font-semibold">{dateLabel}</span>
                            <span className="font-medium text-gray-500">
                              {weekday}
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {tooltip && (
              <div className="absolute bottom-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl pointer-events-none whitespace-nowrap z-20">
                <span className="font-semibold">{tooltip.label}</span>
                {" — "}
                {tooltip.value > 0
                  ? `${Number(tooltip.value).toLocaleString("he-IL")} כניסות`
                  : "אין נתונים"}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default NationalParkHeatmapChart;
